import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Download, Lock, Plus } from 'lucide-react';
import { auth as authApi, categories as catApi, exportApi, tags as tagApi } from '@/api/endpoints';
import type { ItemCondition } from '@/types/api';
import { useAuth } from '@/hooks/useAuth';
import { getStoredThemeMode, setThemeMode, type ThemeMode } from '@/lib/theme';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tag } from '@/components/ui/tag';
import { Chip } from '@/components/ui/chip';
import { loadToken } from '@/api/client';
import { CONDITIONS } from './ItemFormPage';

const successBannerClass =
  'rounded-control bg-[var(--color-success-bg)] px-3 py-2 text-sm text-[var(--color-success)]';

export function SettingsPage() {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  // SettingsPage only ever renders behind RequireAuth, so user is always set here.
  const { user, updateUser } = useAuth();
  const tagsQuery = useQuery({ queryKey: ['tags'], queryFn: tagApi.list });
  const cats = useQuery({ queryKey: ['categories'], queryFn: catApi.list });
  const [newTag, setNewTag] = useState('');

  const [mode, setMode] = useState<ThemeMode>(() => getStoredThemeMode());
  const onModeChange = (next: ThemeMode) => {
    setThemeMode(next);
    setMode(next);
  };

  const [defCcy, setDefCcy] = useState(user!.defaultCurrency);
  const [defCond, setDefCond] = useState<ItemCondition>(user!.defaultCondition);
  const [defaultsSaved, setDefaultsSaved] = useState(false);

  const updateDefaults = useMutation({
    mutationFn: () =>
      authApi.updateProfile({
        // The backend still requires firstName/lastName on every PATCH, even
        // though this action only changes the defaults below.
        firstName: user!.firstName ?? '',
        lastName: user!.lastName ?? '',
        defaultCurrency: defCcy,
        defaultCondition: defCond,
      }),
    onSuccess: (updated) => {
      updateUser(updated);
      setDefaultsSaved(true);
    },
  });

  const createTag = useMutation({
    mutationFn: () => tagApi.create(newTag),
    onSuccess: () => {
      setNewTag('');
      void qc.invalidateQueries({ queryKey: ['tags'] });
    },
  });

  const deleteTag = useMutation({
    mutationFn: (id: number) => tagApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tags'] }),
  });

  const onSubmitDefaults = (e: FormEvent) => {
    e.preventDefault();
    if (updateDefaults.isPending) return;
    setDefaultsSaved(false);
    updateDefaults.mutate();
  };

  const downloadExport = async () => {
    const token = loadToken();
    const res = await fetch(exportApi.url, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'zula-export.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold">{t('settings.title')}</h1>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.language')}</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2">
          <Button variant={i18n.language?.startsWith('tr') ? 'primary' : 'secondary'} onClick={() => i18n.changeLanguage('tr')}>Türkçe</Button>
          <Button variant={i18n.language?.startsWith('en') ? 'primary' : 'secondary'} onClick={() => i18n.changeLanguage('en')}>English</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.appearance')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div role="radiogroup" aria-label={t('settings.theme')} className="flex gap-2">
            <Chip role="radio" aria-checked={mode === 'light'} selected={mode === 'light'} onClick={() => onModeChange('light')}>
              {t('settings.themeLight')}
            </Chip>
            <Chip role="radio" aria-checked={mode === 'dark'} selected={mode === 'dark'} onClick={() => onModeChange('dark')}>
              {t('settings.themeDark')}
            </Chip>
            <Chip role="radio" aria-checked={mode === 'system'} selected={mode === 'system'} onClick={() => onModeChange('system')}>
              {t('settings.themeSystem')}
            </Chip>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.defaults')}</CardTitle>
          <CardDescription>{t('settings.defaultsDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmitDefaults} className="space-y-4" aria-label="settings-defaults-form">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-1">
                <Label htmlFor="defCcy">{t('settings.defaultCurrency')}</Label>
                <Input id="defCcy" placeholder="TRY" maxLength={3} value={defCcy} onChange={(e) => setDefCcy(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="defCond">{t('settings.defaultCondition')}</Label>
                <select id="defCond" value={defCond}
                        onChange={(e) => setDefCond(e.target.value as ItemCondition)}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                  {CONDITIONS.map((c) => <option key={c} value={c}>{t(`items.conditions.${c}`)}</option>)}
                </select>
              </div>
            </div>
            {defaultsSaved && (
              <div role="status" aria-live="polite" className={successBannerClass}>
                {t('settings.defaultsSaved')}
              </div>
            )}
            <Button type="submit" loading={updateDefaults.isPending}>{t('common.save')}</Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.tags')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <form
            className="flex gap-2"
            onSubmit={(e) => { e.preventDefault(); if (newTag.trim()) createTag.mutate(); }}
          >
            <Input value={newTag} onChange={(e) => setNewTag(e.target.value)} placeholder={t('settings.tagPlaceholder')} />
            <Button type="submit" loading={createTag.isPending} disabled={!newTag.trim()} icon={<Plus />}>{t('common.create')}</Button>
          </form>
          <div className="flex flex-wrap gap-2">
            {(tagsQuery.data ?? []).map((tg) => (
              <Chip
                key={tg.id}
                onRemove={() => deleteTag.mutate(tg.id)}
                removeLabel={t('settings.deleteTag', { name: tg.name })}
                disabled={deleteTag.isPending}
              >
                {tg.name}
              </Chip>
            ))}
            {(tagsQuery.data ?? []).length === 0 && <span className="text-sm text-muted-foreground">—</span>}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.categories')}</CardTitle>
          <CardDescription>{(cats.data ?? []).length} kategori</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-2 md:grid-cols-3">
          {(cats.data ?? []).map((c) => (
            <Tag key={c.id} tone={c.system ? 'neutral' : 'brand'} title={c.system ? t('settings.systemCategory') : undefined}>
              {c.system && <Lock className="h-3 w-3 shrink-0" aria-hidden="true" />}
              {i18n.language?.startsWith('tr') ? c.nameTr : c.nameEn}
            </Tag>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('settings.exportData')}</CardTitle>
          <CardDescription>{t('settings.exportDataDesc')}</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={downloadExport}><Download className="mr-2 h-4 w-4" />{t('settings.exportData')}</Button>
        </CardContent>
      </Card>
    </div>
  );
}
